import PropTypes from 'prop-types'
import { isNil } from 'ramda'
import {
  getFlagName,
  getTypeName,
  getStateName,
  getPriorityName,
  getAlarmDescription,
  getFollowupDescription,
  getNotesDescription,
  getNoteBodyDescription,
  getTagsDescription,
  getQueueDescription,
  getDeliverableDescription
} from 'pages/Ticket/components/AuditLogs/helpers'
import {
  parseTaskSnapshot,
  parseTaskNames,
  diffTaskSnapshots,
  describeTaskNamesChange
} from 'pages/Ticket/components/AuditLogs/taskHelpers'

const fieldResolver = field => {
  const wrongType = id => id
  // console.log('fieldResolver', field, typeof field)

  switch (field) {
    case 'ticket_flag_id':
      return getFlagName
    case 'ticket_state_id':
      return getStateName
    case 'ticket_type_id':
      return getTypeName
    case 'priority':
      return getPriorityName
    case 'alarm':
      return getAlarmDescription
    case 'follow_up_data':
      return getFollowupDescription
    case 'assigned_to_id':
      return () => 'Queue'
    case 'notes':
      return getNotesDescription
    case 'note_body':
      return getNoteBodyDescription
    case 'tag_names':
      return getTagsDescription
    case 'queue_name':
      return getQueueDescription
    case 'deliverables':
      return getDeliverableDescription
    default:
      return wrongType
  }
}

const actionResolver = (field, changedFrom, parentAction) => {
  switch (field) {
    case 'ticket_flag_id':
      return 'changed Flag'
    case 'ticket_state_id':
      return 'changed Status'
    case 'ticket_type_id':
      return 'changed Subject'
    case 'priority':
      return 'changed Priority'
    case 'assigned_to_id':
      return 'changed Assignee'
    case 'follow_up_data':
      return 'changed Follow Up'
    case 'notes':
      return 'changed Notes'
    case 'alarm':
      return 'changed Alarm'
    case 'archived':
      if (changedFrom === 'false') return 'archived this Ticket'
      return 'unarchived this Ticket'
    case 'parent_ticket_id':
      return 'changed the parent Ticket'
    case 'symptom_name':
      return 'changed Symptom Name'
    case 'tag_names':
      return 'changed Tags'
    case 'queue_name':
      if (parentAction === 'assigned_by_queue')
        return 'pulled ticket from Queue'
      return 'changed Queue'
    case 'deliverables':
      return 'changed Deliverables'
    case 'note_body':
      return 'changed Journal entry'
    default:
      return field
  }
}

const createdResolver = log => {
  switch (log.field) {
    case 'ticket_type_id':
      return `with Subject ${log.getChangedTo}`
    case 'assigned_to_id':
      return `assigned to ${log.getChangedTo}`
    default:
      return ''
  }
}

const taskActionResolver = (oldSnapshot, newSnapshot) => {
  if (!oldSnapshot && newSnapshot) return 'added a task'
  if (oldSnapshot && !newSnapshot) return 'removed a task'
  return 'updated a task'
}

const taskNamesActionResolver = change => {
  switch (change.mode) {
    case 'reassign':
      return 'reassigned tasks'
    case 'added':
      return 'added tasks'
    case 'removed':
      return 'removed tasks'
    default:
      return 'changed tasks'
  }
}

class AuditLogChangeset {
  constructor(init) {
    if (isNil(init)) return undefined
    this._parent_action = init.parent_action
    this._changes = init.changes
    this._display_changes = init.display_changes
    this._field = init.field
    this._notes = init.notes
  }

  static shape = {
    parent_action: PropTypes.string,
    changes: PropTypes.array,
    display_changes: PropTypes.array,
    field: PropTypes.string,
    notes: PropTypes.string
  }

  get parent_action() {
    return this._parent_action
  }

  get changes() {
    return this._changes || []
  }

  get display_changes() {
    return this._display_changes || []
  }

  get field() {
    return this._field
  }

  get notes() {
    return this._notes
  }

  // Calculable
  get isCreatedAction() {
    return this.parent_action === 'created'
  }

  get isTaskField() {
    return this.field === 'task'
  }

  get isTaskNamesField() {
    return this.field === 'task_names'
  }

  get taskChangedFromSnapshot() {
    const source = this.display_changes[0] ?? this.changes[0]
    return parseTaskSnapshot(source)
  }

  get taskChangedToSnapshot() {
    const source = this.display_changes[1] ?? this.changes[1]
    return parseTaskSnapshot(source)
  }

  get taskDiff() {
    return diffTaskSnapshots(
      this.taskChangedFromSnapshot,
      this.taskChangedToSnapshot
    )
  }

  get taskNamesChange() {
    const oldNames = parseTaskNames(this.changes[0])
    const newNames = parseTaskNames(this.changes[1])
    return describeTaskNamesChange(oldNames, newNames)
  }

  get getAction() {
    if (this.isTaskField)
      return taskActionResolver(
        this.taskChangedFromSnapshot,
        this.taskChangedToSnapshot
      )
    if (this.isTaskNamesField)
      return taskNamesActionResolver(this.taskNamesChange)
    if (this.isCreatedAction) return 'created the ticket'
    return actionResolver(this.field, this.changes[0], this.parent_action)
  }

  get getChangedFrom() {
    if (!this.display_changes[0])
      return fieldResolver(this.field)(this.changes[0])

    return this.display_changes[0]
  }

  get getChangedTo() {
    if (!this.display_changes[1])
      return fieldResolver(this.field)(this.changes[1])

    return this.display_changes[1]
  }

  get shouldHideChanges() {
    if (this.isTaskField || this.isTaskNamesField) return false
    if (this.getChangedFrom === this.getChangedTo) return true
    return ['source_id', 'source_type', 'symptom_id', 'notes'].includes(
      this.field
    )
  }

  get shouldHideDescription() {
    return ['archived'].includes(this.field)
  }

  get getAssignedNotes() {
    return ['assigned_to_id'].includes(this.field) && this.notes
  }

  get getSanitizedNotes() {
    if (!this.notes) return ''

    const regex = /(<([^>]+)>)/gi
    return this.notes.replace(regex, '')
  }

  get getDescription() {
    if (this.isCreatedAction) return createdResolver(this)
    return `${this.getChangedFrom} → ${this.getChangedTo}`
  }

  get isTaglike() {
    return ['deliverables', 'tag_names'].includes(this.field)
  }

  get isCollapsable() {
    return this.isTaglike || ['note_body'].includes(this.field)
  }
}

export default AuditLogChangeset
